/**
 * Z-Index Management
 *
 * Centralized z-index values để đảm bảo layer ordering consistency
 * và dễ dàng maintain về sau.
 */

export const Z_INDEX: Record<string, number> = {
  // Base layers
  base: 0,
  content: 1,

  // UI Elements
  dropdown: 10,
  tooltip: 50,
  popover: 30,

  // Overlays
  modal: 9999,
  toast: 10000,
  splash: 99999,

  // Special cases
  debug: 9998, // For development tools
} as const;

export type ZIndexLayer = keyof typeof Z_INDEX;

/**
 * Get z-index value by layer name
 */
export const getZIndex = (layer: ZIndexLayer): number => {
  return Z_INDEX[layer];
};

/**
 * Generate Tailwind z-index class
 */
export const getZIndexClass = (layer: ZIndexLayer): string => {
  const value = Z_INDEX[layer];

  // Use Tailwind's built-in z-index classes when possible
  switch (value) {
    case 0:
      return 'z-0';
    case 1:
      return 'z-1';
    case 10:
      return 'z-10';
    case 20:
      return 'z-20';
    case 30:
      return 'z-30';
    case 9999:
      return 'z-[9999]';
    case 10000:
      return 'z-[10000]';
    case 99999:
      return 'z-[99999]';
    case 9998:
      return 'z-[9998]';
    default:
      return `z-[${value}]`;
  }
};

/**
 * Validate z-index ordering
 * Useful for development/testing
 */
export const validateZIndexOrder = (): boolean => {
  const values = Object.values(Z_INDEX);
  const sortedValues = [...values].sort((a, b) => a - b);

  return JSON.stringify(values) === JSON.stringify(sortedValues);
};
